import React, { useState, useEffect, useMemo, useRef } from "react";
import { Formik, Form } from "formik";
import ProfileHeader from "../../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { routes } from "../../../../../constants/routes";
import ServiceForm from "./service-form";
import ServicePreview from "./service-preview";
import { ServiceFormValues } from "../service-management-type";
import { serviceValidationSchema } from "./validation";
import "./create-service.scss";
import { CreateServiceProps } from "../service-management-type";
import ApproveOrRejectRequestPopup from "../../provider-management/provider-requests/approve-or-reject-popup";
import {
  serviceFormInitialValues,
  formatPayloadForService,
} from "./create-service-helper";
import {
  createService,
  getServices,
  updateService,
} from "../../../../../api/admin/service-management";
import { AlertComponent } from "../../../../../common/components";
import { useSetLoading } from "../../../../../common/hooks/appearance";

const CreateService: React.FC<CreateServiceProps> = ({
  setIsCreateServiceOpen,
  setAlertMessageForListPage,
  setIsAlertPopupOpenForListPage,
  setAlertTypeForListPage,
  setLoadingForListPage,
  actionType,
  serviceId,
  fetchServices,
  setCurrentServiceId,
  isCreateServiceOpen,
  setActionType,
}) => {
  const [serviceData, setServiceData] = useState<ServiceFormValues | null>(
    null
  );
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [error, setError] = useState("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState<boolean>(false);
  const [isEditClicked, setIsEditClicked] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [formInitialValues, setFormInitialValues] = useState<ServiceFormValues>(
    serviceFormInitialValues
  );
  const serviceFormRef = useRef<any>(null);
  const [formikInstance, setFormikInstance] = useState<any>(null);

  const handleCleanUp = () => {
    setServiceData(null);
    setBreadcrumbLink(breadcrumbLinkForCreate);
    setIsEditClicked(false);
    setCategoryIds([]);
    setFormInitialValues(serviceFormInitialValues);
    setCurrentServiceId?.(null);
    setActionType?.("create");
    // Reset the Formik form using stored instance or ref
    if (formikInstance && formikInstance?.resetForm) {
      formikInstance.resetForm({
        values: serviceFormInitialValues,
      });
    }

  };

  const breadcrumbLinkForCreate = [
    {
      label: "Service Management",
      link: routes.SERVICE_LIST,
      handleClick: () => {
        handleCleanUp();
        setIsCreateServiceOpen(false);
      },
    },
    {
      label: "Service List",
      link: routes.SERVICE_LIST,
      handleClick: () => {
        handleCleanUp();
        setIsCreateServiceOpen(false);
      },
    },
    {
      label: "Create Service",
      link: "#",
      handleClick: () => {
        handleCleanUp();
        setIsCreateServiceOpen(false);
      },
    },
  ];
  const [breadcrumbLink, setBreadcrumbLink] = useState(breadcrumbLinkForCreate);

  const handleBackArrowClick = () => {
    handleCleanUp();
    setIsCreateServiceOpen(false);
  };

  const createServiceApi = async (data: any) => {
    setLoadingForListPage?.(true);
    try {
      //add categoryIds to data
      data.categoryIds = categoryIds;
      const payload = formatPayloadForService(data);
      const response = serviceId ? await updateService(serviceId, payload) : await createService(payload);
      if (response.data?.success) {
        setIsConfirmPopupOpen(false);
        setAlertMessageForListPage?.(`Service ${serviceId ? "updated" : "created"} successfully.`);
        setAlertTypeForListPage?.("success");
        setIsAlertPopupOpenForListPage?.(true);
        fetchServices?.();
        handleCleanUp();
        setIsCreateServiceOpen(false);
      }
      // You can add further logic here, like showing a success message or redirecting
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to create service."
      );
      setAlertType("error");
      setAlertOpen(true);
      console.error("Error creating service:", error);
    } finally {
      setLoadingForListPage?.(false);
    }
  };

  const handleFormSubmit = (values: ServiceFormValues) => {
    setServiceData(values);
    setIsConfirmPopupOpen(true);
  };

  const handleFormCancel = () => {
    // Logic to handle form cancellation (e.g., navigate back or reset form)
    setIsEditClicked(false);
  };

  useEffect(() => {
    if (actionType === "view" && isCreateServiceOpen) {
      setIsEditClicked(false);
      setBreadcrumbLink([
        {
          label: "Service Management",
          link: routes.SERVICE_LIST,
          handleClick: () => {
            handleCleanUp();
            setIsCreateServiceOpen(false);
          },
        },
        {
          label: "Service List",
          link: routes.SERVICE_LIST,
          handleClick: () => {
            handleCleanUp();
            setIsCreateServiceOpen(false);
          },
        },
        {
          label: serviceData?.name || "View Service",
          link: "#",
          handleClick: () => {
            handleCleanUp();
            setIsCreateServiceOpen(false);
          },
        },
      ]);
    } else if (actionType === "edit") {
      setBreadcrumbLink([
        {
          label: "Service Management",
          link: routes.SERVICE_LIST,
          handleClick: () => {
            handleCleanUp();
            setIsCreateServiceOpen(false);
          },
        },
        {
          label: "Service List",
          link: routes.SERVICE_LIST,
          handleClick: () => {
            handleCleanUp();
            setIsCreateServiceOpen(false);
          },
        },
        {
          label: serviceData?.name || "Edit Service",
          link: "#",
          handleClick: () => {
            handleCleanUp();
            setIsCreateServiceOpen(false);
          },
        },
      ]);
    } else {
      setBreadcrumbLink(breadcrumbLinkForCreate);
    }
  }, [actionType, isCreateServiceOpen, serviceData?.name]);

  const fetchServiceDetails = async (id: number) => {
    setLoading(true);
    try {
      const response = await getServices({}, id);
      if (response.status === 200) {
        const serviceDetails = response?.data?.data;

        const formattedData = {
          id: serviceDetails.id,
          name: serviceDetails.name,
          code: serviceDetails.code,
          description: serviceDetails.description,
          category: serviceDetails.categories.map((cat: any) => cat.name),
          needtypes: [
            ...new Set(
              serviceDetails.categories
                .flatMap((cat: any) => cat.needtype || [])
                .map((type: string) => {
                  if (type === "pre_need") return "Preneed";
                  if (type === "at_need") return "Immediate Need";
                  return type;
                })
            ),
          ],
          status: serviceDetails.status === true || serviceDetails.status === "active" ? true : false,
          minPrice: serviceDetails.basePrice?.min || 0,
          maxPrice: serviceDetails.basePrice?.max || 0,
          taxApplicable: serviceDetails.taxApplicable,
          commissionPercent: serviceDetails.commissionPercentage,
          commissionValue: serviceDetails.commissionFixed,
          subServices: serviceDetails.subServices.map((subService: any) => ({
            id: subService.id || null,
            name: subService.name,
            description: subService.description,
            minPrice: subService.minPrice || 0,
            maxPrice: subService.maxPrice || 0,
            status: subService.status,
          })),
          merchandises: serviceDetails.merchandises.map((merchandise: any) => ({
            id: merchandise.id || null,
            name: merchandise.name,
            code: merchandise.code,
            description: merchandise.description,
            minPrice: merchandise.minPrice,
            maxPrice: merchandise.maxPrice,
            status: merchandise.status
          })),
        }
        setFormInitialValues(formattedData);
        setCategoryIds(serviceDetails.categories.map((cat: any) => cat.id));
        setServiceData(formattedData);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch service details."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (serviceId && (actionType === "edit" || actionType === "view")) {
      fetchServiceDetails(serviceId);
    }
  }, [serviceId, actionType]);

  const getHeadingText = () => {
    if (actionType === "create") return "Create New Service";
    if (actionType === "edit") return "Edit Service";
    if (actionType === "view" && !isEditClicked) return "View Service";
    if (actionType === "view" && isEditClicked) return "Edit Service";
    return "";
  }

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="create-service-main-wrapper">
      <ProfileHeader
        breadcrumbLink={breadcrumbLink}
        showBackArrow
        handleBackArrowClick={handleBackArrowClick}
      />

      <Formik
        enableReinitialize
        initialValues={formInitialValues}
        validationSchema={serviceValidationSchema}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={(values) => {
          handleFormSubmit(values);
        }}
      >
        {(formik) => {
          // Store the formik instance for use in cleanup
          if (!formikInstance) {
            setFormikInstance(formik);
          }

          return (
            <Form>
              <div className="create-service-form-wrapper">
                <div className="heading-wrapper">
                  <h3 className="create-service-heading">{getHeadingText()}</h3>

                  <div className="buttons-wrapper">
                    {(actionType !== "view" || isEditClicked) && (
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={() => {
                          handleCleanUp();
                          handleFormCancel();
                          setIsCreateServiceOpen(false);
                        }}
                      >
                        Cancel
                      </button>
                    )}
                    {actionType === "view" && !isEditClicked && (
                      <button
                        type="button"
                        className="submit-button"
                        onClick={() => {
                          setIsEditClicked(true);
                        }}
                      >
                        Edit
                      </button>
                    )}
                    {(actionType !== "view" || isEditClicked) && (
                      <button
                        type="submit"
                        className="submit-button"
                        disabled={actionType === "create" ? !formik.dirty : false}
                      >
                        {`${actionType === "create" ? "Create Service" : "Edit Service"}`}
                      </button>
                    )}
                  </div>
                </div>

                <div className="create-service-layout">
                  <ServiceForm
                    isReadOnly={actionType === "view" && !isEditClicked}
                    setAlertOpen={setAlertOpen}
                    setAlertType={setAlertType}
                    setAlertMessage={setAlertMessage}
                    setCategoryIds={setCategoryIds}
                    isEdit={isEditClicked}
                  />{" "}
                  {/* NO <Form> inside */}
                  <ServicePreview />
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
      <ApproveOrRejectRequestPopup
        open={isConfirmPopupOpen}
        title={`Confirm Service ${actionType === "create" ? "Creation" : "Update"}`}
        note={`Are you sure you want to ${actionType === "create" ? "create" : "update"} this service with the provided details? Click continue to proceed.`}
        buttonText="Continue"
        cancelBtnText="Cancel"
        onOkay={() => {
          createServiceApi(serviceData);
        }}
        onCancel={() => {
          setIsConfirmPopupOpen(false);
        }}
        fieldName="approve"
      />
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => {
          setAlertOpen(false)
        }}
      />
    </div>
  );
};

export default CreateService;
